
import { formatNumber, getFollowerValue, getTooltipData } from "./index"; // Import helpers

// ⭐ NEW — Safe percentage calculation that correctly handles negative previous values
const safePercentageChange = (current, previous) => {
  if (isNaN(current) || isNaN(previous)) return 0;

  // Avoid divide-by-zero
  if (previous === 0) {
    if (current === 0) return 0;
    return 100; // moving from 0 to positive is 100% increase
  }

  const diff = current - previous;

  // ⭐ FIX 1: If previous is negative and current is positive → treat as positive movement
  if (previous < 0 && current > 0) {
    return Math.abs((diff / Math.abs(previous)) * 100);
  }

  // ⭐ FIX 2: If both values are negative but moving upward toward 0 → positive trend
  if (previous < 0 && current > previous) {
    return Math.abs((diff / Math.abs(previous)) * 100);
  }

  // Normal case
  return ((current - previous) / previous) * 100;
};

export default function AudienceKeyMetrics({
  validData,
  previousValidData,
  adjustedCurrentData,
  adjustedPreviousData,
  metricKey,
  predictedMetricKey,
  metricName,
  currSpanDays,
  prevSpanDays,

  // Existing props
  totalImpressions,
  prevTotalImpressions,
  totalReach,
  prevTotalReach,
}) {
  // -------------------------------------------------------
  // Audience Metrics Calculations
  // -------------------------------------------------------

  // Total Followers
  const totalFollowers =
    getFollowerValue(validData.at(-1), metricKey, predictedMetricKey).value ?? 0;
  const prevFollowers =
    getFollowerValue(previousValidData.at(-1), metricKey, predictedMetricKey).value ?? 0;

  const totalFollowersChangePct = safePercentageChange(
    totalFollowers,
    prevFollowers
  );

  // Audience Growth Calculation
  // Hybrid approach: Use Iterative Subtraction (Curr - Prev) for reliability.
  // BUT fill the Day 0 gap (missing T-1) using Granular Net Change for that specific day, 
  // as granular metrics might not be available for all records globally.

  /* eslint-disable no-console */
  const calculateGrowthFromData = (data, adjustedData, label) => {
    if (!data || data.length === 0) {
      // console.log(`[AudienceKeyMetrics] ${label}: No data available`);
      return 0;
    }

    // LOG REQUESTED BY USER: Print FULL dataset
    // if (adjustedData && adjustedData.length > 0) {
    //   console.group(`[AudienceKeyMetrics] ${label} - Dataset Inspection`);
    //   console.log("Full Adjusted Data:", adjustedData);
    //   console.groupEnd();
    // }

    let totalGrowth = 0;

    data.forEach((item, index) => {
      const currentVal = getFollowerValue(item, metricKey, predictedMetricKey);
      let diff = 0;

      if (index === 0) {
        // console.group(`[AudienceKeyMetrics] ${label} - Day 0 (${item.date})`);

        // SPECIAL HANDLING FOR FIRST DAY
        let hasT1Baseline = false;

        // 1. Check if adjustedData provides a valid T-1 baseline (different date implies T-1)
        if (adjustedData && adjustedData.length > 0) {
          const baselineItem = adjustedData[0];
          // console.log("Baseline Date:", baselineItem.date, "Item Date:", item.date);

          // If dates differ -> Baseline is T-1
          // If dates match -> Baseline is T-0 (so T-1 is missing)
          if (baselineItem.date !== item.date) {
            const prevVal = getFollowerValue(baselineItem, metricKey, predictedMetricKey);
            if (currentVal.value !== null && prevVal.value !== null) {
              diff = currentVal.value - prevVal.value;
              hasT1Baseline = true;
              // console.log("Using T-1 Baseline. Diff:", diff);
            }
          } else {
            // console.log("Baseline Comparison: Dates Match. Missing T-1.");
          }
        } else {
          // console.log("No Adjusted Data Available.");
        }

        // 2. Fallback: If T-1 missing, use Granular Metric for Day 0
        if (!hasT1Baseline) {
          // console.log("Attempting Granular Fallback...");
          const pm = item.profileMetrics || item.pageMetrics || {};
          // console.log("Metrics:", pm);

          // Use net_followers if available, else gain - loss
          if (pm.net_followers !== undefined) {
            diff = pm.net_followers;
            // console.log("Used net_followers:", diff);
          } else {
            const gain = pm.followers_gain ?? pm.follower_gain ?? 0;
            const loss = pm.followers_lost ?? 0;
            // console.log("Gain:", gain, "Loss:", loss);

            if (gain !== 0 || loss !== 0) { // Only apply if we have non-zero metrics
              diff = gain - loss;
              // console.log("Used Gain-Loss Calculation:", diff);
            } else {
              // console.log("Granular Net Change is 0.");
            }
          }
        }
        // console.log("Final Diff for Day 0:", diff);
        console.groupEnd();

      } else {
        // Normal case (Index > 0): Use previous day in the current dataset
        const prevVal = getFollowerValue(data[index - 1], metricKey, predictedMetricKey);
        if (currentVal.value !== null && prevVal.value !== null) {
          diff = currentVal.value - prevVal.value;
        }
      }

      totalGrowth += diff;
    });

    // console.log(`[AudienceKeyMetrics] Final Total Growth for ${label}:`, totalGrowth);
    return totalGrowth;
  };
  /* eslint-enable no-console */

  const currGrowth = calculateGrowthFromData(validData, adjustedCurrentData, "Current Period");
  const prevGrowth = calculateGrowthFromData(previousValidData, adjustedPreviousData, "Previous Period");

  const growthChangePct = safePercentageChange(currGrowth, prevGrowth);

  // Restore definitions needed for Growth Rate %
  const firstCurrentAdj =
    getFollowerValue(adjustedCurrentData[0], metricKey, predictedMetricKey).value ?? 0;

  const lastCurrentAdj =
    getFollowerValue(adjustedCurrentData.at(-1), metricKey, predictedMetricKey).value ?? 0;

  const audienceGrowthRatePct = firstCurrentAdj
    ? ((lastCurrentAdj - firstCurrentAdj) / firstCurrentAdj) * 100
    : 0;

  // Avg Daily Growth
  const averageDailyGrowth = currGrowth / currSpanDays;
  const prevAvgDailyGrowth = prevGrowth / prevSpanDays;

  const avgDailyGrowthChangePct = safePercentageChange(
    averageDailyGrowth,
    prevAvgDailyGrowth
  );

  // Total Impressions
  const impressionsChangePct = safePercentageChange(
    totalImpressions,
    prevTotalImpressions
  );

  // Total Reach
  const reachChangePct = safePercentageChange(
    totalReach,
    prevTotalReach
  );

  // -------------------------------------------------------
  // ⭐ NEW: Granular Gained/Lost Logic (Instagram mainly)
  // -------------------------------------------------------
  const calculateSum = (data, key) => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => {
      // Check profileMetrics, pageMetrics, and channelMetrics
      const pm = item.profileMetrics || item.pageMetrics || item.channelMetrics || {};

      let val = 0;
      if (key === "follower_gain") {
        val = pm.follower_gain ?? pm.followers_gain ?? pm.subscribersGained ?? item.followers_gain ?? 0;
      } else if (key === "followers_lost") {
        val = pm.follower_lost ?? pm.followers_lost ?? pm.subscribersLost ?? item.followers_lost ?? 0;
      }

      // follower_lost is often returned as a negative number by some APIs, or positive by others.
      // We will treat "Lost" as a magnitude (positive number) for the card display.
      return sum + Math.abs(val);
    }, 0);
  };

  // Check if we even HAVE granular data in the current set
  const hasGranularData = adjustedCurrentData.some((item) => {
    const pm = item.profileMetrics || item.pageMetrics || item.channelMetrics || {};
    return (
      pm.follower_gain !== undefined ||
      pm.followers_gain !== undefined ||
      pm.subscribersGained !== undefined ||
      item.followers_gain !== undefined
    );
  });

  let gainedCards = [];

  if (hasGranularData) {
    // Current Period
    const totalGained = calculateSum(adjustedCurrentData, "follower_gain");
    const totalLost = calculateSum(adjustedCurrentData, "followers_lost");

    // Previous Period
    const prevGained = calculateSum(adjustedPreviousData, "follower_gain");
    const prevLost = calculateSum(adjustedPreviousData, "followers_lost");

    // % Change
    const gainedChangePct = safePercentageChange(totalGained, prevGained);
    const lostChangePct = safePercentageChange(totalLost, prevLost);

    gainedCards = [
      {
        title: "Followers Gained",
        color: "accent",
        icon: "person_add",
        count: formatNumber(totalGained),
        fullCount: totalGained,
        description: "Total new followers gained during this period.",
        percentage: {
          color: gainedChangePct >= 0 ? "success" : "error",
          amount: `${gainedChangePct.toFixed(2)}%`,
          tooltip: getTooltipData(
            totalGained,
            prevGained,
            adjustedCurrentData,
            adjustedPreviousData
          ),
        },
      },
      {
        title: "Followers Lost",
        color: "accent",
        icon: "person_remove",
        count: formatNumber(totalLost),
        fullCount: totalLost,
        description: "Total followers lost during this period.",
        percentage: {
          // For "Lost", a DECREASE in lost followers is GOOD (Success), Increase is BAD (Error)
          color: lostChangePct <= 0 ? "success" : "error",
          // Flip the arrow logic? Usually UI libraries handle arrow up/down based on sign.
          // Here we just color it. Less loss = Green. More loss = Red.
          amount: `${lostChangePct.toFixed(2)}%`,
          tooltip: getTooltipData(
            totalLost,
            prevLost,
            adjustedCurrentData,
            adjustedPreviousData
          ),
        },
      },
    ];
  }

  // -------------------------------------------------------
  // Build Metric Cards (UNCHANGED)
  // -------------------------------------------------------
  return [
    {
      title: "Total Views",
      color: "accent",
      icon: "visibility",
      count: formatNumber(totalImpressions),
      fullCount: totalImpressions,
      description: "The total number of times your content was viewed.",
      percentage: {
        color: impressionsChangePct >= 0 ? "success" : "error",
        amount: `${impressionsChangePct.toFixed(2)}%`,
        tooltip: getTooltipData(
          totalImpressions,
          prevTotalImpressions,
          validData,
          previousValidData
        ),
      },
    },

    // ⭐ NEW REACH CARD (UNCHANGED)
    {
      title: "Total Reach",
      color: "accent",
      icon: "public",
      count: formatNumber(totalReach),
      fullCount: totalReach,
      description: "The number of unique people who saw your content.",
      percentage: {
        color: reachChangePct >= 0 ? "success" : "error",
        amount: `${reachChangePct.toFixed(2)}%`,
        tooltip: getTooltipData(
          totalReach,
          prevTotalReach,
          validData,
          previousValidData
        ),
      },
    },

    {
      title: `Total ${metricName}`,
      color: "accent",
      icon: "group",
      count: formatNumber(totalFollowers),
      fullCount: totalFollowers,
      description: `The total number of people following your profile.`,
      percentage: {
        color: totalFollowersChangePct >= 0 ? "success" : "error",
        amount: `${totalFollowersChangePct.toFixed(2)}%`,
        tooltip: getTooltipData(
          totalFollowers,
          prevFollowers,
          validData,
          previousValidData
        ),
      },
    },

    {
      title: "Audience Growth",
      color: "accent",
      icon: "trending_up",
      count: formatNumber(currGrowth),
      fullCount: currGrowth,
      description: "The net change in followers during the selected period.",
      percentage: {
        color: growthChangePct >= 0 ? "success" : "error",
        amount: `${growthChangePct.toFixed(2)}%`,
        tooltip: getTooltipData(
          currGrowth,
          prevGrowth,
          validData,
          previousValidData
        ),
      },
    },

    // ⭐ INSERT NEW CARDS HERE
    ...gainedCards,

    {
      title: "Growth Rate (%)",
      color: "accent",
      icon: "percent",
      count: `${audienceGrowthRatePct.toFixed(2)}%`,
      description: "The percentage rate at which your audience is growing."
    },

    {
      title: "Avg Daily Growth",
      color: "accent",
      icon: "calendar_today",
      count: formatNumber(averageDailyGrowth),
      fullCount: averageDailyGrowth,
      description: "The average number of new followers gained per day.",
      percentage: {
        color: avgDailyGrowthChangePct >= 0 ? "success" : "error",
        amount: `${avgDailyGrowthChangePct.toFixed(2)}%`,
        tooltip: getTooltipData(
          averageDailyGrowth,
          prevAvgDailyGrowth,
          validData,
          previousValidData
        ),
      },
    },
  ];
}
